/**
 * Core API configuration and fetch infrastructure
 */

import { snakeCase } from 'lodash-es'

import { API_FETCH_OPTIONS } from '../../types/api'

/**
 * Base API configuration
 */
export const API_CONFIG = {
  baseUrl: '/api', // Vite proxy will redirect this to backend
  timeout: 30000, // 30 second timeout
}

/**
 * Convert an object's keys from camelCase to snake_case
 * Uses lodash's snakeCase for robust case conversion
 */
export function convertParamsToSnakeCase(
  params: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      result[snakeCase(key)] = value
    }
  }
  return result
}

/**
 * Shared fetch implementation with timeout, abort-signal forwarding,
 * 401/session-expired handling, and error normalisation.
 * `parseBody` lets callers choose between JSON, text, etc.
 */
async function apiFetchImpl<T>(
  url: string,
  options: RequestInit,
  parseBody: (response: Response) => Promise<T>,
): Promise<T> {
  const controller = new AbortController()
  let timeoutFired = false
  const timeoutId = setTimeout(() => {
    timeoutFired = true
    controller.abort()
  }, API_CONFIG.timeout)

  // If a signal is provided in options, listen for its abort event and abort our controller
  if (options.signal) {
    const originalSignal = options.signal
    if (originalSignal.aborted) {
      controller.abort()
    } else {
      const abortHandler = () => controller.abort()
      originalSignal.addEventListener('abort', abortHandler, { once: true })

      // Clean up listener if our own timeout fires first
      controller.signal.addEventListener(
        'abort',
        () => {
          originalSignal.removeEventListener('abort', abortHandler)
        },
        { once: true },
      )
    }
  }

  try {
    const response = await fetch(url, {
      ...API_FETCH_OPTIONS,
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      // Special handling for 401 Unauthorized with expired token
      if (response.status === 401) {
        const authError = response.headers.get('X-Auth-Error')

        // Only trigger redirect flow for expired or missing tokens (legitimate user session timeout)
        // Don't save/redirect for invalid tokens (likely attacker or malformed request)
        if (authError === 'token-expired' || authError === 'token-missing') {
          // Save current location before redirect
          const currentPath = window.location.pathname + window.location.search
          sessionStorage.setItem('auth_redirect_after_login', currentPath)

          // Dispatch custom event for AuthContext to handle
          window.dispatchEvent(
            new CustomEvent('auth:session-expired', {
              detail: {
                errorType: authError,
                currentPath,
              },
            }),
          )

          throw new Error('Session expired')
        }

        // For other 401s (invalid token, missing auth), just fail normally
        throw new Error('Unauthorized')
      }

      // Try to parse error response body for backend error message
      let errorData
      try {
        errorData = await response.json()
      } catch {
        // If JSON parsing fails, fall through to generic error
      }

      // Check for backend message OUTSIDE the try-catch
      if (errorData) {
        const backendMessage = errorData.message || errorData.error
        if (backendMessage) {
          throw new Error(backendMessage)
        }
      }

      // Fallback to generic error if no message in response body
      throw new Error(
        `API request failed: ${response.status} ${response.statusText}`,
      )
    }

    return parseBody(response)
  } catch (error) {
    clearTimeout(timeoutId)

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        // Only treat as timeout if the abort was caused by our internal timeout
        // Check the timeoutFired flag to distinguish from external cancellation
        if (timeoutFired) {
          throw new Error(`API request timeout: ${url}`)
        }
        // Otherwise, it's an external cancellation (e.g., from React Query) - rethrow as-is
        throw error
      }
      // Re-throw the error as-is (already has backend message or appropriate error text)
      throw error
    }

    throw new Error(`Unknown API error: ${url}`)
  }
}

/**
 * Generic API fetch function with error handling — returns parsed JSON.
 * `tenant` is optional; when omitted the URL is built without a tenant prefix
 * (used for super_admin endpoints such as GET /cloud/accounts).
 */
export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {},
  tenant?: string,
): Promise<T> {
  const url = tenant
    ? `${API_CONFIG.baseUrl}/${tenant}${endpoint}`
    : `${API_CONFIG.baseUrl}${endpoint}`
  return apiFetchImpl<T>(url, options, r => r.json() as Promise<T>)
}

/**
 * Like apiFetch but returns the response body as plain text.
 * Use for endpoints that return non-JSON content (e.g. YAML/template downloads).
 */
export async function apiFetchText(
  endpoint: string,
  options: RequestInit = {},
  tenant: string,
): Promise<string> {
  const url = `${API_CONFIG.baseUrl}/${tenant}${endpoint}`
  return apiFetchImpl<string>(url, options, r => r.text())
}

/**
 * Like apiFetch but for write endpoints that return 204 No Content (no body).
 * Shares the same auth/session-expired and error-normalisation logic.
 */
export async function apiFetchVoid(
  endpoint: string,
  options: RequestInit = {},
  tenant: string,
): Promise<void> {
  const url = `${API_CONFIG.baseUrl}/${tenant}${endpoint}`
  return apiFetchImpl<void>(url, options, () => Promise.resolve())
}

/**
 * Health check endpoint
 */
export async function healthCheck(): Promise<{
  status: string
  service: string
}> {
  const response = await fetch('/api/health')
  if (!response.ok) {
    throw new Error(`Health check failed: ${response.status}`)
  }
  return response.json()
}

/**
 * Test API connectivity
 */
export async function testApiConnectivity(): Promise<boolean> {
  try {
    await healthCheck()
    return true
  } catch (error) {
    console.error('❌ API connectivity test failed:', error)
    return false
  }
}
